import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  flexRender,
  tableFeatures,
  useTable,
  type ColumnDef,
} from "@tanstack/react-table"
import { ArrowDown, Download, Eye, EyeOff, Loader2 } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import { PkBadge } from "../shared/PkBadge"
import { SectionLabel } from "../shared/SectionLabel"
import {
  COLUMN_STATE_CELL_BG,
  COLUMN_STATE_HEADER_BG,
  COLUMN_STATE_LETTER,
  getColumnState,
  isPrimaryKeyAssignment,
  scopedColumnKey,
  SKIPPED_ASSIGNMENT,
  SKIPPED_COLUMN_CLASS,
  type MappingColumn,
} from "./mappingUtils"

const ROW_INDEX_ID = "__row_index__"

// Negative margin keeps the padded hit area from growing its row.
const ICON_BUTTON_CLASS =
  "-my-1 shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"

export type PreviewRow = Record<string, string>

// Core only: the preview neither sorts nor resizes (TanStack Table 9
// features are opt-in).
const previewTableFeatures = tableFeatures({})
type PreviewColumnDef = ColumnDef<typeof previewTableFeatures, PreviewRow>

interface MappingPreviewTableProps {
  /** Label shown next to the section title (e.g. file name or sheet name). */
  title: string
  columns: MappingColumn[]
  /** Already zipped rows, keyed by `column.columnRef`. */
  rows: PreviewRow[]
  totalRows: number
  isLoading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  activeColumnRef: string | null
  onActiveColumnChange: (columnRef: string) => void
  /** Keyed by `scopedColumnKey`, not the bare column name (T1). */
  resolveAssignment: (
    columnKey: string,
    aiAssigned: string | null,
  ) => string | null
  /** Skips a column, or brings a skipped one back to its AI suggestion.
   *  Omit to hide the per-column skip control. */
  onAssignmentChange?: (columnKey: string, assignment: string | null) => void
  /** Domain-specific label for a resolved assignment (e.g. "Articles · ID"). */
  formatTargetLabel: (assignment: string | null) => string | null
  /** Placeholder shown in an unmapped column's header (e.g. "— Pick an entity —"). */
  placeholderLabel: string
  /** Visible worksheet names, in order. Omit/empty to hide the sheet tab strip. */
  sheetNames?: string[]
  activeSheetName?: string | null
  onSheetChange?: (sheetName: string) => void
  /** Downloads the whole source file. Omit to hide the header's download control. */
  onDownload?: () => void
  /** Disables the download control while the caller prepares the file. */
  isDownloading?: boolean
}

/**
 * Right pane: spreadsheet-style live preview. Purely presentational — callers
 * own fetching/pagination and hand in already-resolved `rows`, so the same
 * component works for any source (staged import rows, questionnaire sheets…).
 */
export function MappingPreviewTable({
  title,
  columns,
  rows: data,
  totalRows,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  activeColumnRef,
  onActiveColumnChange,
  resolveAssignment,
  onAssignmentChange,
  formatTargetLabel,
  placeholderLabel,
  sheetNames = [],
  activeSheetName,
  onSheetChange,
  onDownload,
  isDownloading = false,
}: MappingPreviewTableProps) {
  const { translate } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const showSheetTabs = sheetNames.length > 1

  const tableColumns = useMemo<PreviewColumnDef[]>(() => {
    const indexColumn: PreviewColumnDef = {
      id: ROW_INDEX_ID,
      header: () => null,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.index + 1}
        </span>
      ),
    }

    const dataColumns = columns.map<PreviewColumnDef>((column) => {
      const columnKey = scopedColumnKey(column.sheetName, column.columnName)
      const assignment = resolveAssignment(columnKey, column.aiAssigned)
      const target = formatTargetLabel(assignment)
      const isPk = isPrimaryKeyAssignment(assignment)
      const isSkipped = assignment === SKIPPED_ASSIGNMENT
      const skipLabel = translate(
        isSkipped ? "MC_MD_MAP_INCLUDE_COLUMN" : "MC_MD_MAP_SKIP_COLUMN",
      )
      return {
        id: column.columnRef,
        accessorKey: column.columnRef,
        header: () => (
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium text-foreground">
                {column.columnName}
              </span>
              {isPk && <PkBadge />}
              {onAssignmentChange && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={skipLabel}
                        // Bubbles on purpose: the header's own click makes this
                        // the active column, as picking from the list's dropdown does.
                        onClick={() =>
                          onAssignmentChange(
                            columnKey,
                            isSkipped ? column.aiAssigned : SKIPPED_ASSIGNMENT,
                          )
                        }
                        className={cn(
                          ICON_BUTTON_CLASS,
                          "ml-auto",
                          // Out of the way until the column is hovered, active,
                          // or already skipped (so it can be brought back).
                          !isSkipped &&
                            column.columnRef !== activeColumnRef &&
                            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                        )}
                      >
                        {isSkipped ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{skipLabel}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div
              className={cn(
                "truncate font-normal",
                isSkipped
                  ? "text-muted-foreground"
                  : target
                    ? "text-primary"
                    : COLUMN_STATE_LETTER["unmapped"],
              )}
            >
              {target ?? placeholderLabel}
            </div>
          </div>
        ),
        cell: ({ getValue }) => (
          <span className="truncate text-foreground">{getValue<string>()}</span>
        ),
      }
    })

    return [indexColumn, ...dataColumns]
  }, [
    columns,
    resolveAssignment,
    onAssignmentChange,
    formatTargetLabel,
    placeholderLabel,
    activeColumnRef,
    translate,
  ])

  const table = useTable({
    features: previewTableFeatures,
    data,
    columns: tableColumns,
  })

  const columnStates = Object.fromEntries(
    columns.map((column) => [
      column.columnRef,
      getColumnState(
        resolveAssignment(
          scopedColumnKey(column.sheetName, column.columnName),
          column.aiAssigned,
        ),
        column.aiAssigned,
      ),
    ]),
  )

  // Bring the highlighted column into view when the selection changes.
  useEffect(() => {
    if (!activeColumnRef) return
    // Safe today since columnRef is generated A-Z, but CSS.escape() matches
    // the same defensive fix as MappingColumnList/MasterDataList.
    const cell = scrollRef.current?.querySelector(
      `[data-column-ref="${CSS.escape(activeColumnRef)}"]`,
    )
    cell?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    })
  }, [activeColumnRef])

  return (
    <div ref={scrollRef} className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <SectionLabel>{translate("MC_MD_MAP_LIVE_PREVIEW")}</SectionLabel>
        <span className="font-mono truncate text-xs text-muted-foreground">
          {title}
        </span>
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {totalRows > 0
            ? translate("MC_MD_MAP_PREVIEW_ROWS", { count: data.length })
            : isLoading
              ? ""
              : translate("MC_MD_MAP_PREVIEW_ROWS", { count: 0 })}
        </span>
        {onDownload && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={translate("MC_MD_MAP_DOWNLOAD_FILE")}
                  onClick={onDownload}
                  disabled={isDownloading}
                  className={ICON_BUTTON_CLASS}
                >
                  {isDownloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {translate("MC_MD_MAP_DOWNLOAD_FILE")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-auto">
        <Table containerClassName="overflow-visible" className="text-xs">
          <TableHeader>
            {/* Excel-style column-letter strip */}
            <TableRow className="hover:bg-transparent">
              {table.getFlatHeaders().map((header) => (
                <TableHead
                  key={`letter-${header.id}`}
                  data-column-ref={header.column.id}
                  onClick={() =>
                    header.column.id !== ROW_INDEX_ID &&
                    onActiveColumnChange(header.column.id)
                  }
                  className={cn(
                    "font-mono h-6 border-b bg-muted/40 py-0 text-center align-middle font-normal text-muted-foreground/70",
                    header.column.id !== ROW_INDEX_ID && "cursor-pointer",
                    header.column.id === ROW_INDEX_ID && "w-10",
                    COLUMN_STATE_LETTER[
                      columnStates[header.column.id] ?? "default"
                    ],
                    header.column.id === activeColumnRef &&
                      "bg-primary/15 text-primary",
                    columnStates[header.column.id] === "skipped" &&
                      SKIPPED_COLUMN_CLASS,
                  )}
                >
                  {header.column.id === ROW_INDEX_ID ? null : header.column.id}
                </TableHead>
              ))}
            </TableRow>

            {/* Column name + target field */}
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    onClick={() =>
                      header.column.id !== ROW_INDEX_ID &&
                      onActiveColumnChange(header.column.id)
                    }
                    className={cn(
                      header.column.id !== ROW_INDEX_ID &&
                        "group cursor-pointer",
                      header.column.id === ROW_INDEX_ID && "w-10 text-center",
                      COLUMN_STATE_HEADER_BG[
                        columnStates[header.column.id] ?? "default"
                      ],
                      header.column.id === activeColumnRef && "bg-primary/10",
                      columnStates[header.column.id] === "skipped" &&
                        SKIPPED_COLUMN_CLASS,
                    )}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getAllCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "max-w-[200px] truncate whitespace-nowrap",
                      cell.column.id === ROW_INDEX_ID && "text-center",
                      COLUMN_STATE_CELL_BG[
                        columnStates[cell.column.id] ?? "default"
                      ],
                      cell.column.id === activeColumnRef && "bg-primary/6",
                      columnStates[cell.column.id] === "skipped" &&
                        SKIPPED_COLUMN_CLASS,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {hasMore && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="sticky left-0 flex w-full shrink-0 cursor-pointer items-center justify-center gap-1.5 border-t bg-background py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/50 disabled:cursor-not-allowed"
          >
            {isLoadingMore ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
            {translate("MC_MD_MAP_LOAD_MORE")}
          </button>
        )}
      </div>

      {showSheetTabs && (
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-t bg-muted/30 px-2 py-1.5">
          {sheetNames.map((sheetName) => (
            <button
              key={sheetName}
              type="button"
              onClick={() => onSheetChange?.(sheetName)}
              className={cn(
                "shrink-0 rounded-t-sm border-b-2 px-3 py-1 text-xs font-medium transition-colors",
                sheetName === activeSheetName
                  ? "border-primary bg-background text-primary"
                  : "border-transparent text-muted-foreground hover:bg-background/60 hover:text-foreground",
              )}
            >
              {sheetName}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
